import { ErrorMessage, Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { CardDescription } from "~/shared/components/ui/card";
import { Label } from "~/shared/components/ui/label";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import networkService from "~/services/network.service";
import {
  ADD_LICENSE,
  CHECK_ORGANIZATION,
  CREATE_ORGANIZATION,
  CREATE_SUBSCRIPTION,
  GET_PLANS,
  LOGIN_API,
  REGISTRATION_API,
  SERVICE_ID,
} from "~/shared/constants/api";
import { REGISTRATION_ERROR } from "~/shared/constants/errormessage";
import {
  BUTTON_TEXT_SIGNING_UP,
  BUTTON_TEXT_SIGNUP,
} from "~/shared/constants/label";
import useAuthStore from "~/store/auth.store";
import { PlanId, PlanType, UserRole } from "~/shared/config";
import { handleError } from "~/shared/utils/errors.util";
import HeartFocusIcon from "~/shared/icon/heartfocus.icon";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import TabletIcon from "~/shared/icon/tablet.icon";
import brandImg from "../../../assets/images/brand-image.png"
import { countryPhoneCodes } from '~/shared/constants/countryPhoneCodes';
import PasswordValidationInput from '~/shared/components/password-validation-input';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("planId");
  const { user, setAuthState, updateUserInfo } = useAuthStore((state) => ({
    user: state.user,
    setAuthState: state.setAuthState,
    updateUserInfo: state.updateUserInfo,
  }));
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countryPhoneCodes.find(c => c.prefix === 1) || countryPhoneCodes[0]);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (!user) return;

    if (user?.role === UserRole.Admin) {
      navigate("/home");
    } else {
      navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (!isCountryDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  const checkOrg = async (values: any) => {
    try {
      const organizationData = await networkService.post<any>(CHECK_ORGANIZATION, {
        name: values.organization,
      });
      const isAvailableToUse = organizationData.data.orgNameInUse;
      return !isAvailableToUse;
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
      return false;
    }
  };

  const createLicense = async function (
    subscriptionId: string,
    deviceId: string
  ) {
    try {
      if (deviceId) {
        const licenseResponse = await networkService.post<any>(
          `${ADD_LICENSE}/${subscriptionId}/${deviceId}`,
          null
        );
        toast.success("License generated successfully.");
        return licenseResponse;
      } else {
        const licenseResponse = await networkService.post<any>(
          `${ADD_LICENSE}/${subscriptionId}`,
          null
        );
        toast.success("License generated successfully.");
        return licenseResponse;
      }
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
      return null;
    }
  };

  const createSubscription = async function (
    organizationId: string,
    planId: string
  ) {
    try {
      const subscriptionResponse = await networkService.post<any>(
        `${CREATE_SUBSCRIPTION}/${organizationId}/${planId}`,
        {
          nickName: `Subscription to ${planId}`,
        }
      );
      if (subscriptionResponse) {
        toast.success(
          "Subscription fetched successfully. Generating license..."
        );
        return subscriptionResponse.data.subscription?.id;
      }
      return null;
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
      return null;
    }
  };

  const getFreePlanId = async () => {
    try {
      const serviceId = SERVICE_ID;
      if (serviceId) {
        const response = await networkService.get<any>(
          `${GET_PLANS}/${serviceId}/plans`
        );
        const plan = response.data.results.filter(
          (plan) => plan.isActive && plan.type === PlanType.Free
        );
        return plan[0]?.id;
      }
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
    }
  };

  const handleRegistration = async (values: any, { setSubmitting }: any) => {
    setSubmitting(true);
    try {
      const orgFound = await checkOrg(values);

      if (!orgFound) {
        toast.error("Organization name already in use");
        return;
      }

      const response = await networkService.post<any>(REGISTRATION_API, {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phone: values.phoneNumber
          ? values.phoneNumber.replace(/[^+\d]/g, '')
          : undefined,
      });

      if (response) {
        toast.success("User registered successfully");

        const loginResponse = await networkService.post<any>(LOGIN_API, {
          email: values.email,
          password: values.password,
        });

        setAuthState({
          accessToken: loginResponse.data.accessToken,
          user: {
            userId: loginResponse.data.userId,
            role: loginResponse.data.role,
            organizationId: "",
          },
        });

        const createOrganizationResponse = await networkService.post<any>(
          CREATE_ORGANIZATION,
          {
            name: values.organization,
            description: `${values.organization} description`,
          }
        );
        updateUserInfo({ organizationId: createOrganizationResponse.data?.id });
        if (createOrganizationResponse) {
          toast.success("Organization created successfully");
          navigate("/purchase");
          return;
        }
      }
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
      setRegistrationError(REGISTRATION_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    organization: Yup.string()
      .required("This field is required.")
      .min(3, "Organization must be at least 3 characters"),
    firstName: Yup.string()
      .required("This field is required.")
      .min(3, "First name must be at least 3 characters"),
    lastName: Yup.string()
      .required("This field is required.")
      .min(3, "Last name must be at least 3 characters"),
    email: Yup.string()
      .email("Oops! This email format looks incorrect.")
      .required("This field is required."),
    countryCode: Yup.string().test('countryCode', 'Country code is required.', val => !val || val.startsWith('+')).nullable(),
    phoneNumber: Yup.string()
      .test('phone', 'Please enter a valid phone number in the correct format.', function (value) {
        if (!value) return true;
        // Remove all non-digit characters except +
        let digits = value.replace(/[^\d+]/g, '');
        // For US, must be +1XXXXXXXXXX (11 digits, starts with +1)
        if (digits.startsWith('+1')) {
          return /^\+1\d{10}$/.test(digits);
        }
        // For other countries, keep your existing logic or adjust as needed
        // ...
        return true;
      }),
    password: Yup.string()
      .required("This field is required.")
      .min(12, "Password must be at least 12 characters long.")
      .matches(/[0-9]/, "Must contain a number.")
      .matches(/[A-Z]/, "Must contain an uppercase letter.")
      .matches(/[a-z]/, "Must contain a lowercase letter.")
      .matches(/[^A-Za-z0-9]/, "Must contain a special character."),
  });

  return (
    <div className="flex min-h-screen font-space-grotesk">
      {/* Left Container - Form */}
      <div className="flex md:w-2/5 w-full flex-col justify-between items-center px-6 lg:pt-10 lg:pb-10 z-10 py-10">
        {/* Header */}
        <div className="font-sans text-2xl font-bold leading-tight text-center bg-radial-gradient bg-clip-text">
          <HeartFocusIcon />
        </div>
        
        {/* Form */}
        <div className="w-full xl:w-4/5 flex flex-col mt-8 2xl:mt-14">
          <Formik
            initialValues={{
              organization: "",
              firstName: "",
              lastName: "",
              email: "",
              countryCode: "+1",
              phoneNumber: "",
              password: "",
            }}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validationOnChange={false}
            onSubmit={(values, actions) => {
              handleRegistration(values, actions);
            }}
          >
            {({ isSubmitting, errors, touched, setFieldValue, values }) => {
              return (
                <Form className="flex flex-col gap-12 2xl:gap-12">
                  {/* Header */}
                  <CardDescription className="text-black flex flex-col gap-2">
                    <span className="text-4xl font-bold">Welcome!</span>
                    <span className="block text-base font-normal">
                      Sign up to get a HeartFocus Education limited time FREE license.
                    </span>
                  </CardDescription>
                  
                  {/* Registration Error */}
                  {registrationError && (
                    <p className="text-[#F87171] text-sm font-space-grotesk font-medium flex items-center gap-3 border border-[#F87171] px-4 py-3 rounded-lg bg-[#FEF2F2] -my-4">
                      <AlertCircle size={16} />
                      <span>{registrationError}</span>
                    </p>
                  )}
                  
                  <div className={`flex flex-col gap-7 2xl:gap-6 ${errors.email || errors.firstName || errors.lastName || errors.organization || errors.password ? "lg:gap-1" : "gap-4 lg:gap-4 2xl:gap-6"}`}>
                    {/* Organization Field */}
                    <div className="flex w-full flex-col gap-1 items-start">
                      <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                        Organization
                      </Label>
                      <p className="text-xs text-[#79797F] font-normal -mt-1 mb-1">
                        The organization name needs to be unique. Try adding
                        letters and numbers if needed.
                      </p>
                      <Field
                        name="organization"
                        type="text"
                        className={`px-4 py-2.5 w-full rounded-[0.5rem] placeholder-gray input-text focus:outline-none bg-transparent ${errors.organization && touched.organization ? "border border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : "border-[1px] border-[#D0D0D6]"}`}
                        placeholder="Enter the name of your organization"
                      />
                      <ErrorMessage
                        name="organization"
                        component="p"
                        children={(errorMessage) => (
                          <div className="text-[#F87171] text-xs flex gap-1 items-center">
                            <AlertCircle size={14} />
                            <span>{errorMessage}</span>
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="flex gap-8 md:gap-3 flex-col md:flex-row">
                      {/* First Name Field */}
                      <div className="flex w-full flex-col items-start gap-1">
                        <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                          First Name
                        </Label>
                        <Field
                          name="firstName"
                          type="text"
                          className={`px-4 py-2.5 w-full rounded-[0.5rem] placeholder-gray input-text focus:outline-none bg-transparent ${errors.firstName && touched.firstName ? "border border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : "border-[1px] border-[#D0D0D6]"}`}
                          placeholder="Enter your First Name"
                        />
                        <ErrorMessage
                          name="firstName"
                          component="p"
                          children={(errorMessage) => (
                            <div className="text-[#F87171] text-xs flex gap-1 items-center">
                              <AlertCircle size={14} />
                              <span>{errorMessage}</span>
                            </div>
                          )}
                        />
                      </div>

                      {/* Last Name Field */}
                      <div className="flex w-full flex-col items-start gap-1">
                        <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                          Last Name
                        </Label>
                        <Field
                          name="lastName"
                          type="text"
                          className={`px-4 py-2.5 w-full rounded-[0.5rem] placeholder-gray input-text focus:outline-none bg-transparent ${errors.lastName && touched.lastName ? "border border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : "border-[1px] border-[#D0D0D6]"}`}
                          placeholder="Enter your Last Name"
                        />
                        <ErrorMessage
                          name="lastName"
                          component="p"
                          children={(errorMessage) => (
                            <div className="text-[#F87171] text-xs flex gap-1 items-center">
                              <AlertCircle size={14} />
                              <span>{errorMessage}</span>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    
                    {/* Email Field */}
                    <div className="flex w-full flex-col gap-1 items-start">
                      <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                        Email
                      </Label>
                      <Field
                        name="email"
                        type="email"
                        className={`px-4 py-2.5 w-full rounded-[0.5rem] placeholder-gray input-text focus:outline-none bg-transparent ${errors.email && touched.email ? "border border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : "border-[1px] border-[#D0D0D6]"}`}
                        placeholder="Enter your email address"
                      />
                      <ErrorMessage
                        name="email"
                        component="p"
                        children={(errorMessage) => (
                          <div className="text-[#F87171] text-xs flex gap-1 items-center">
                            <AlertCircle size={14} />
                            <span>{errorMessage}</span>
                          </div>
                        )}
                      />
                    </div>
                    
                    {/* Phone Number Field */}
                    <div className="flex w-full flex-col gap-1 items-start">
                      <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                        Phone number
                      </Label>
                      <div className="flex gap-2 w-full">
                        <div className="relative" ref={countryDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                            className={`px-3 py-3 rounded-[0.5rem] border-[1px] border-[#D0D0D6] bg-transparent w-24 focus:outline-none flex items-center justify-between ${errors.countryCode && touched.countryCode ? "border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : ""}`}
                          >
                            <span>+{selectedCountry.prefix}</span>
                            <svg
                              className={`w-4 h-4 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {isCountryDropdownOpen && (
                            <div className="absolute bg-white-a700 z-10 mt-1 w-96 max-h-60 overflow-auto rounded-[0.5rem] border border-[#D0D0D6] shadow-lg">
                              {countryPhoneCodes.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-gray-200 flex items-center"
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setFieldValue('countryCode', `+${country.prefix}`);
                                    setIsCountryDropdownOpen(false);
                                  }}
                                >
                                  <span>{country.country_name}</span>
                                  <span className="ml-2">{`(+${country.prefix})`}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Field
                          name="phoneNumber"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              maxLength={selectedCountry.prefix === 1 ? 17 : 22}
                              value={field.value}
                              onFocus={e => {
                                if (selectedCountry.prefix === 1) {
                                  if (!field.value || !field.value.startsWith(`+1 `)) {
                                    setFieldValue('phoneNumber', `+1 `);
                                  }
                                } else {
                                  if (!field.value || !field.value.startsWith(`+${selectedCountry.prefix} `)) {
                                    setFieldValue('phoneNumber', `+${selectedCountry.prefix} `);
                                  }
                                }
                              }}
                              onChange={e => {
                                let val = e.target.value;
                                if (selectedCountry.prefix === 1) {
                                  // Remove all non-digit except +
                                  val = val.replace(/[^\d+]/g, '');
                                  // Remove duplicate +1 if present
                                  val = val.replace(/^\+1+/, '+1');
                                  // Remove any extra +
                                  val = val.replace(/(\+1)(?=\+1)/g, '');
                                  // Remove +1 prefix for formatting
                                  let digits = val.replace('+1', '');
                                  // Only keep up to 10 digits
                                  digits = digits.replace(/\D/g, '').slice(0, 10);
                                  let formatted = '';
                                  if (digits.length > 0) {
                                    formatted = '(' + digits.slice(0, 3);
                                  }
                                  if (digits.length >= 4) {
                                    formatted += ') ' + digits.slice(3, 6);
                                  }
                                  if (digits.length >= 7) {
                                    formatted += '-' + digits.slice(6, 10);
                                  }
                                  // Remove trailing formatting if not enough digits
                                  formatted = formatted.replace(/\(\) /, '');
                                  formatted = formatted.replace(/\(\d{0,3}\) $/, (m) => m.trim());
                                  setFieldValue('phoneNumber', `+1 ${formatted}`.trim());
                                } else {
                                  const prefix = `+${selectedCountry.prefix} `;
                                  if (!val.startsWith(prefix)) {
                                    val = prefix + val.replace(/[^ -9]/g, "");
                                  }
                                  val = prefix + val.slice(prefix.length).replace(/[^\d]/g, "");
                                  setFieldValue('phoneNumber', val);
                                }
                              }}
                              className={`flex-1 px-4 py-2.5 rounded-[0.5rem] border-[1px] border-[#D0D0D6] placeholder-gray input-text focus:outline-none bg-transparent ${errors.phoneNumber && touched.phoneNumber ? "border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : ""}`}
                              placeholder={selectedCountry.prefix === 1 ? "+1 (XXX) XXX-XXXX" : selectedCountry.format_excluding_country_prefix || "Enter phone number"}
                              autoComplete="tel"
                              inputMode="numeric"
                            />
                          )}
                        />
                      </div>
                      <div className="flex gap-2 mt-1">
                        <ErrorMessage
                          name="countryCode"
                          component="p"
                          children={(errorMessage) => (
                            <div className="text-[#F87171] text-xs flex gap-1 items-center">
                              <AlertCircle size={14} />
                              <span>{errorMessage}</span>
                            </div>
                          )}
                        />
                        <ErrorMessage
                          name="phoneNumber"
                          component="p"
                          children={(errorMessage) => (
                            <div className="text-[#F87171] text-xs flex gap-1 items-center">
                              <AlertCircle size={14} />
                              <span>{errorMessage === 'Invalid phone number.' ? 'Please enter a valid phone number in the correct format.' : errorMessage}</span>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    
                    {/* Password Field */}
                    <div className="flex w-full flex-col items-start gap-1">
                      <Label className="text-[#1B1B20] font-space-grotesk text-base font-semibold mb-1">
                        Password
                      </Label>
                      <p className="text-xs text-[#79797F] font-normal -mt-1 mb-1">
                        Must be at least 12 characters long, contain a number, uppercase, lowercase, and special character.
                      </p>
                      <div className="relative w-full">
                        <Field
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          className={`px-4 py-2.5 w-full rounded-[0.5rem] placeholder-gray input-text focus:outline-none bg-transparent ${errors.password && touched.password
                            ? "border border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]"
                            : "border-[1px] border-[#D0D0D6]"
                            }`}
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onFocus={() => setShowPasswordValidation(true)}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-3 top-3 text-gray-500"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      {errors.password && touched.password && errors.password === "This field is required." && (
                        <div className="text-[#F87171] text-xs flex gap-1 items-center mt-1">
                          <AlertCircle size={14} />
                          <span>{errors.password}</span>
                        </div>
                      )}
                      <PasswordValidationInput
                        value={values.password}
                        show={showPasswordValidation}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5 mt-6">
                      <button
                        type="submit"
                        className="w-full font-semibold bg-white-a700 rounded-full p-1.5 hover:bg-light-angular-gradient"
                        disabled={isSubmitting}>
                        {isSubmitting
                          ? BUTTON_TEXT_SIGNING_UP
                          : BUTTON_TEXT_SIGNUP}
                      </button>
                    </div>

                    <div className="flex flex-col gap-7 mt-7 2xl:mt-6">
                      <hr className="w-full border-t border-gray-300" />
                      <div className="w-full justify-center">
                        <Link
                          to="/login"
                          className="text-[#000] font-space-grotesk text-[16px] font-normal leading-[24px]">
                          Already have an account?{" "}
                          <span className="font-semibold underline">Login</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        
        {/* Terms and conditions */}
        <div className="w-full xl:w-4/5 flex flex-col mt-12 2xl:items-center">
          <p className="text-xs text-gray-700 text-center">
            By clicking on 'Sign up' you agree to Heartfocus's Legal notices & terms of use the{" "}
            <a
              className="text-gray-700 underline"
              href="https://www.heartfocus.ai/education-software-license-agreement" target="_blank">
              HeartFocus Education Software license agreement
            </a>
            . DESKi processes the data collected to create an account for you to access the portal and use the services. To find out more about the management of your personal data and to exercise your rights, please refer to the document{" "}
            <a
              href="https://www.heartfocus.ai/portal-terms-conditions"
              className="text-gray-700 underline" target="_blank">
              Legal notices & terms of use.
            </a>
          </p>
        </div>
      </div>
      
      {/* Right Container - Background Image */}
      <div className="w-3/5 hidden md:flex relative overflow-hidden">
        <div className="relative w-full h-full flex flex-col">
          {/* Background Image - Full Height */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/Gradientcropped.png')",
            }}
          />
          
          {/* Content Overlay */}
          <div className="relative z-10 flex flex-col items-center justify-center text-center h-full px-8 py-12">
            <div className="text-4xl font-bold mb-2 pt-5">
              We believe No Heart Can Wait&trade;
            </div>
            <p className="text-lg mb-6">
              Join us in bringing heart health to the whole world.
            </p>
            <img
              src={brandImg}
              alt="Brand Image"
              className="xl:w-[70%] md:w-[95%] mb-4"
            />
            <p className="pb-5">
              Learn more about HeartFocus{" "}
              <a href="https://heartfocus.ai" className="underline" target="_blank">
                here.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;